import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Text } from 'rebass/styled-components';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Button, SmallLoader, CheckboxOld } from 'shared/components';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import useApolloClient from 'shared/hooks/use-apollo-client';
import afterSubmit from 'src/checkout/actions/after-submit';
import useErnie from 'shared/hooks/use-ernie';

import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import { AmplitudeCategory, AmplitudeEvents, getCartEventProperties, useAmplitude } from 'shared/hooks/use-amplitude';
import onClickSubmit from 'src/checkout/actions/on-click-submit';
import ContactSupportModal from 'shared/modals/contact-support';
import PublicEnv from 'shared/utils/public-env';
import { tracker } from 'src/utils/analytics';
import { useAerosyncPreAuthContext } from 'src/payments/components/aerosync-pre-auth/hooks/use-aerosync-pre-auth';
import { MobileEcommTermsLink } from 'src/mobile-ecomm/components/terms-link';
import { useDutchiePay } from 'src/payments/hooks';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import useLogRocket from 'src/hooks/use-logrocket';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import useFeatureFlags from 'hooks/use-feature-flags';
import { useObserver } from 'mobx-react-lite';
import { ModalNames, useModals } from 'components/modals';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS } from 'shared/constants/turnstile';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';

import { ConnectionStatus, DUTCHIE_PAY, PBB_NAME } from 'src/payments/constants';
import { CreateOrderDocument } from 'src/gql/graphql';
import ColoradoWarning from './components/colorado-warning';
import { categorizePaymentOptions, getUnenrolledAlertMessage } from '../sections/payment/shared/utils';

const CA_BROCHURE_URL = 'https://cdn.cannabis.ca.gov/wp-content/uploads/sites/2/2024/12/SB-540-color.pdf';

const CaliforniaDisclosure = () => (
  <>
    {'. '}
    By submitting this order I acknowledge I was provided access to the state{' '}
    <StyledLink rel='noopener noreferrer' target='_blank' href={CA_BROCHURE_URL}>
      informational brochure
    </StyledLink>{' '}
    as required.
  </>
);

const TermsLink = ({ isMobileEcommApp, termsAndConditionsUrl, text = 'Terms' }) =>
  isMobileEcommApp ? (
    <MobileEcommTermsLink text={text} />
  ) : (
    <StyledLink rel='noopener noreferrer' target='_blank' href={termsAndConditionsUrl}>
      {text}
    </StyledLink>
  );

const PrivacyPolicyLink = ({ privacyPolicyUrl }) => (
  <StyledLink rel='noopener noreferrer' target='_blank' href={privacyPolicyUrl}>
    Privacy Policy
  </StyledLink>
);

const FloridaPrivacyDisclosure = ({ legalInfo, privacyPolicyUrl }) => (
  <>
    You also agree to permit {legalInfo.name} to use your personal identifying information for the sole purpose of
    website purchasing and to their{' '}
    <StyledLink rel='noopener noreferrer' target='_blank' href={privacyPolicyUrl}>
      privacy policy
    </StyledLink>{' '}
    and in compliance with 64ER22-7,F.A.R
  </>
);

const FloridaTermsText = ({ UI, isCheckboxText, termsAndConditionsUrl, privacyPolicyUrl, legalInfo }) => {
  const textAlign = isCheckboxText ? 'left' : 'center';
  const action = isCheckboxText ? 'checking this box' : 'placing an order';

  return (
    <Text textAlign={textAlign} fontSize='11px' lineHeight='17px' color='var(--dcm-checkout-text-muted-color, #909ca7)'>
      By {action} you agree to Dutchie's{' '}
      <TermsLink isMobileEcommApp={UI.isMobileEcommApp} termsAndConditionsUrl={termsAndConditionsUrl} /> and to receive
      automated text messages for order updates.{' '}
      {privacyPolicyUrl && <FloridaPrivacyDisclosure legalInfo={legalInfo} privacyPolicyUrl={privacyPolicyUrl} />}
    </Text>
  );
};

const DefaultTermsText = ({ UI, termsAndConditionsUrl, privacyPolicyUrl, isCaliforniaDispensary }) => (
  <Text textAlign='center' fontSize='11px' lineHeight='17px' color='var(--dcm-checkout-text-muted-color, #909ca7)'>
    By continuing you agree to our{' '}
    <TermsLink
      isMobileEcommApp={UI.isMobileEcommApp}
      termsAndConditionsUrl={termsAndConditionsUrl}
      text='Terms of Service'
    />{' '}
    and consent to receiving automated text messages or other communications for order updates. For information on our
    privacy practices and commitment to protecting your privacy, please review our{' '}
    <PrivacyPolicyLink privacyPolicyUrl={privacyPolicyUrl} />
    {isCaliforniaDispensary && <CaliforniaDisclosure />}
  </Text>
);

function TermsText(props) {
  const {
    UI,
    showFLExplicitConsent = false,
    isCheckboxText,
    legalInfo,
    isCaliforniaDispensary = false,
    customTermsText,
  } = props;

  const termsAndConditionsUrl = legalInfo?.termsAndConditionsUrl || `${PublicEnv.consumerUrl}/terms`;
  const privacyPolicyUrl = legalInfo?.privacyPolicyUrl || `${PublicEnv.consumerUrl}/privacy`;

  return (
    <>
      {showFLExplicitConsent ? (
        <FloridaTermsText
          UI={UI}
          isCheckboxText={isCheckboxText}
          termsAndConditionsUrl={termsAndConditionsUrl}
          privacyPolicyUrl={privacyPolicyUrl}
          legalInfo={legalInfo}
        />
      ) : (
        <DefaultTermsText
          UI={UI}
          termsAndConditionsUrl={termsAndConditionsUrl}
          privacyPolicyUrl={privacyPolicyUrl}
          isCaliforniaDispensary={isCaliforniaDispensary}
        />
      )}
      {customTermsText && <CustomTermsText>{customTermsText}</CustomTermsText>}
    </>
  );
}

function Submit({
  isFloridaDispensary,
  isColoradoDispensary,
  isCaliforniaDispensary,
  blockOrderOnAllotmentFailure = false,
  submitButtonContents = null,
}) {
  const [loading, setLoading] = useState(false);
  const [orderDidSucceed, setOrderDidSucceed] = useState(false);
  const [isFLOptInCompleted, setFLOptInCompleted] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const { Cart, User, UI, Modals } = useStores();
  const isCalculating = useObserver(() => Cart.isCalculating);
  const FeatureFlags = useFeatureFlags();
  const flags = useFlags();
  const Checkout = useCheckout();
  const apolloClient = useApolloClient();
  const showErnie = useErnie();
  const amplitude = useAmplitude();
  const paymentOptions = useDispensaryPaymentMethods();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const checkOnDuty = useDispensaryFlag(`dynamic-delivery-on-duty-check`, Cart.dispensary?.id, false);
  const { isEnrolledDutchiePay, aggregator, needsRelinkBankAccount } = useDutchiePay();
  const { track } = useLogRocket();
  const { openModal } = useModals();
  const { triggerPreAuthFlow } = useAerosyncPreAuthContext();
  const { isCashBack } = useIncentives();
  const setLoadingStates = (value) => {
    setLoading(value);
    Cart.setFreezeDeliveryInfo(value);
  };
  const { onlinePaymentOption, offlinePaymentOptions } = categorizePaymentOptions(paymentOptions ?? []);
  const { events } = useCommerceComponents();

  const enableAerosyncPreAuthCheck = flags['fintech.platform.enable-aerosync-balance-check.rollout'];

  const isTurnstileEnabled = flags['growth.ecomm.enable-turnstile-on-checkout-bool.rollout'] ?? false;

  const turnstileTokenRef = useRef(null);
  const turnstileErrorRef = useRef(null);
  const { TurnstileComponent, executeAndWaitForToken: executeAndWaitForTurnstileToken } = useTurnstile({
    action: TURNSTILE_ACTIONS.CHECKOUT,
    size: 'compact',
  });

  const enableOmniauthLogin = Cart.dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;

  const isEmailRequiredForCurbsideArrival =
    Cart.orderType === 'curbsidePickup' && Cart.dispensary?.featureFlags?.enableArrivals;
  const isDutchiePayEnabledForDispo = Cart.order?.dispensary?.paymentTypesAccepted.dutchiePay;
  const checkoutToken = Cart.order?.checkoutToken;

  const dutchiePayBankError = needsRelinkBankAccount && Cart.order.paymentMethod === 'dutchiePay';
  const dutchiePayNotEnrolled = Cart.order?.paymentMethod === 'dutchiePay' && !isEnrolledDutchiePay;

  // this is called after the order mutation has completed
  const handleOrderPlaced = async (data) => {
    if (data?.createOrderV2?.valid) {
      setOrderDidSucceed(true);
      const { order } = data.createOrderV2;
      if (order.guestCustomer && !order.guestCustomer.email && !!User.email) {
        order.guestCustomer.email = User.email;
      }
      if (order.customer && !order.customer.emails?.length) {
        order.customer.emails = [User.email];
      }

      const roundUpTheChangeEligible = !!Cart.charityFeeDonationId;

      tracker.placedOrder({
        checkoutToken,
        order,
        isDutchiePayEnabledForDispo,
        isEnrolledDutchiePay,
        ...(roundUpTheChangeEligible && {
          roundupForCharityAnalyticsData: {
            roundUpTheChangeEligible,
            roundUpTheChangeFeeDonationId: Cart.charityFeeDonationId,
            roundUpTheChangeSelected: Cart.charityRoundupSelected,
            roundUpTheChangeAmount: Cart.costBreakdown?.roundupForCharityAmount ?? 0,
          },
        }),
      });

      if (isDutchiePayEnabledForDispo) {
        if (Cart.order.paymentMethod === 'dutchiePay') {
          track(`DutchiePay_paid_with_dutchiePay`);
        } else {
          track(`DutchiePay_paid_with_other`);
        }
      }
      const serverOrderThreshold = FeatureFlags.flags['core.cats.server-order-total-threshold'] || 0.0;

      await afterSubmit({
        serverOrder: order,
        showContactSupportModal: () => setSupportModalOpen(true),
        apolloClient,
        showErnie,
        CheckoutCtx: Checkout,
        Cart,
        User,
        UI,
        Modals,
        mobileEcommHandler: () => callNativeMethod(`onOrderComplete`, { orderId: order.orderId }),
        serverOrderThreshold,
      });

      events?.onAfterCheckout?.({ orderNumber: order.orderId });
    }
    DutchiePayEnrollment.setAutoCheckoutAfterBankLinkReconnect(false);
    Cart.setAutoCheckoutAfterBankLinkReconnect(false);
  };

  // build the submitOrder mutation
  const [submitOrder] = useMutation(CreateOrderDocument, {
    onCompleted: handleOrderPlaced,
    client: apolloClient,
    onError: _.noop, // required to properly handle errors
  });

  const handleClickSubmit = async () => {
    if (loading || isCalculating) {
      return;
    }
    setLoadingStates(true);

    if (isTurnstileEnabled) {
      if (!turnstileTokenRef.current) {
        try {
          const token = await executeAndWaitForTurnstileToken({ keepMounted: true, successDelayMs: 1000 });
          turnstileTokenRef.current = token;
        } catch (error) {
          turnstileErrorRef.current = error.message;
        }
      }

      if (turnstileErrorRef.current || !turnstileTokenRef.current || turnstileTokenRef.current?.length === 0) {
        UI.showErnie(
          `Error validating your identity. Please try again. ${
            turnstileErrorRef.current ? `CF: ${turnstileErrorRef.current}` : ''
          }`,
          `danger`
        );
        setLoadingStates(false);
        return;
      }
    }

    const requirePaymentMethodSelection = Cart.dispensary?.storeSettings?.requirePaymentMethodSelection;
    if (requirePaymentMethodSelection && !Cart.order.paymentMethod) {
      Checkout.addErrors([{ card: 'payment', message: 'Select a payment option.' }]);
      setLoadingStates(false);
      return;
    }

    if (dutchiePayBankError) {
      setLoadingStates(false);
      DutchiePayEnrollment.setAutoCheckoutAfterBankLinkReconnect(true);
      openModal(ModalNames.dutchiePayRelinkBankAccount);
      return;
    }
    // If the payment method is DutchiePay and the user is not enrolled, or the order type does not support DutchiePay, show an error
    const paymentMethodIsDutchiePay = Cart.order.paymentMethod === DUTCHIE_PAY;
    const onlinePaymentMethodIsNotDutchiePay = onlinePaymentOption?.value !== DUTCHIE_PAY;
    const checkoutWithDutchiePayDisabled = !isEnrolledDutchiePay || onlinePaymentMethodIsNotDutchiePay;
    if (paymentMethodIsDutchiePay && checkoutWithDutchiePayDisabled) {
      Checkout.addErrors([{ card: 'payment', message: getUnenrolledAlertMessage(offlinePaymentOptions.length > 0) }]);
      setLoadingStates(false);
      return;
    }

    if (dutchiePayNotEnrolled) {
      // if Pay by Bank is the only option, it will be slected automatically
      // but the user may not be enrolled so we need to catch that case
      // if Pay by Bank is not the only option and user is not enrolled it will
      // automatically select the first non-DP option
      UI.showErnie(
        `This dispensary only accepts ${PBB_NAME} for online orders. Click the dutchiePay button to sign up.`,
        `danger`
      );
      setLoadingStates(false);
      return;
    }

    const runAerosyncPreAuthCheck =
      enableAerosyncPreAuthCheck && paymentMethodIsDutchiePay && aggregator === 'aerosync' && Cart.paymentMethodId;
    if (runAerosyncPreAuthCheck) {
      try {
        await triggerPreAuthFlow(Cart.paymentMethodId);
      } catch (error) {
        UI.showErnie(error.message, 'danger');
        setLoadingStates(false);
        User.refetch();
        return;
      }
    }

    // Validate terms acceptance for non-Florida dispensaries that require it
    const requireTermsAcceptance = Cart.dispensary?.storeSettings?.requireTermsAcceptance;
    const showTermsAcceptanceCheckbox = !isFloridaDispensary && requireTermsAcceptance;
    if (showTermsAcceptanceCheckbox && !isTermsAccepted) {
      UI.showErnie('Please accept the Terms and Conditions to continue.', 'danger');
      setLoadingStates(false);
      return;
    }

    const cartEventTracking = getCartEventProperties(Cart, UI);

    const termsAcceptedValue = !isFloridaDispensary && requireTermsAcceptance && isTermsAccepted ? true : null;

    // process submit action
    const { outOfStockItem, returnToMenu, dispensaryCName } = await onClickSubmit(submitOrder, {
      Cart,
      UI,
      User,
      apolloClient,
      showErnie,
      Checkout,
      checkOnDuty,
      isEmailRequiredForCurbsideArrival,
      enableOmniauthLogin,
      tracker,
      flags,
      turnstileToken: turnstileTokenRef.current,
      considerPayerForIncentives: isCashBack,
      termsAccepted: termsAcceptedValue,
    });

    if (!_.isEmpty(Checkout?.errors)) {
      const errorCode = Checkout.errors[0].extensions?.error_code;
      cartEventTracking.checkoutError = _.map(Checkout?.errors, `message`).join(`, `);

      Cart.setAutoCheckoutAfterBankLinkReconnect(false);

      if (errorCode === ConnectionStatus.aggregatorConnectionError) {
        openModal(ModalNames.dutchiePayRelinkBankAccount);
      }

      // Refresh User store enrollment data after PBB payment failures so the
      // connectionStatus reflects any enrollment status changes.
      if (Cart.order.paymentMethod === DUTCHIE_PAY) {
        User.refetch();
      }
    }

    amplitude.log(AmplitudeEvents.checkout.placeOrder, {
      ...cartEventTracking,
      description: 'User clicks "Place Order"',
      category: AmplitudeCategory.checkout,
      outOfStockItem,
    });

    // stop loading.
    setLoadingStates(false);

    if (returnToMenu) {
      UI.showMenu(dispensaryCName, true);
      Cart.setAutoCheckoutAfterBankLinkReconnect(false);
    }
  };

  const showColoradoWarning = isColoradoDispensary;
  const showFLExplicitConsent = isFloridaDispensary && !Cart.saveGuestInfo && Cart.guestCustomer;
  const legalInfo = {
    name: Cart.dispensary.name,
    privacyPolicyUrl: Cart.dispensary.privacyPolicyUrl ?? Cart.dispensary.embedSettings?.privacyPolicyUrl ?? '',
    termsAndConditionsUrl: Cart.dispensary.termsAndConditionsUrl ?? '',
  };
  const customTermsText = Cart.dispensary?.storeSettings?.enableCustomTerms
    ? Cart.dispensary?.storeSettings?.customTermsText
    : null;

  const requireTermsAcceptance = Cart.dispensary?.storeSettings?.requireTermsAcceptance;
  const showTermsAcceptanceCheckbox = !isFloridaDispensary && requireTermsAcceptance;

  let isSubmitEnabled = true;
  if (showFLExplicitConsent && !isFLOptInCompleted) {
    isSubmitEnabled = false;
  }
  if (showTermsAcceptanceCheckbox && !isTermsAccepted) {
    isSubmitEnabled = false;
  }
  if (blockOrderOnAllotmentFailure) {
    isSubmitEnabled = false;
  }

  return (
    <>
      <>
        {showFLExplicitConsent && (
          <StyledCheckbox
            name='setTermsOptIn'
            label={
              <TermsText
                UI={UI}
                showFLExplicitConsent
                isCheckboxText
                legalInfo={legalInfo}
                customTermsText={customTermsText}
              />
            }
            aria-label='By checking this box you agree to Dutchie`s terms of service and privacy policy.'
            data-cy='set-terms-optIn'
            data-test='set-terms-optIn'
            checked={Cart?.order?.guestCustomer?.termsOptIn}
            onChange={({ target }) => {
              if (target.checked) {
                if (!Cart.order.guestCustomer) {
                  Cart.order.guestCustomer = {};
                }
                Cart.order.guestCustomer.termsOptIn = true;
                setFLOptInCompleted(true);
              } else if (Cart?.order?.guestCustomer?.termsOptIn) {
                Cart.order.guestCustomer.termsOptIn = false;
                setFLOptInCompleted(false);
              }
            }}
            color='var(--dcm-checkout-text-color, #6b6f73)'
          />
        )}
        {showTermsAcceptanceCheckbox && (
          <StyledCheckbox
            name='termsAccepted'
            label={
              <Text
                textAlign='left'
                fontSize='11px'
                lineHeight='17px'
                color='var(--dcm-checkout-text-muted-color, #909ca7)'
              >
                I agree to the Terms and Conditions listed below.
              </Text>
            }
            aria-label='I agree to the Terms and Conditions listed below.'
            data-cy='terms-accepted'
            data-test='terms-accepted'
            checked={isTermsAccepted}
            onChange={({ target }) => setIsTermsAccepted(target.checked)}
            color='var(--dcm-checkout-text-color, #6b6f73)'
          />
        )}
      </>
      {showColoradoWarning && <ColoradoWarning />}
      <SubmitButton
        loading={loading || isCalculating}
        orderDidSucceed={orderDidSucceed}
        handleClickSubmit={handleClickSubmit}
        showFLExplicitConsent={showFLExplicitConsent}
        isButtonEnabled={isSubmitEnabled}
        submitButtonContents={submitButtonContents}
        guestCustomer={Cart.guestCustomer}
      />

      {isTurnstileEnabled && <TurnstileComponent />}

      {!showFLExplicitConsent && (
        <TermsText
          UI={UI}
          showFLExplicitConsent={isFloridaDispensary}
          legalInfo={legalInfo}
          isCaliforniaDispensary={isCaliforniaDispensary}
          customTermsText={customTermsText}
        />
      )}
      {supportModalOpen && (
        <ContactSupportModal
          message="Something went wrong with your order, but we're here to help. Please contact dutchie customer support."
          onRequestClose={() => setSupportModalOpen(false)}
        />
      )}
    </>
  );
}

function SubmitButton({ loading, orderDidSucceed, handleClickSubmit, isButtonEnabled, submitButtonContents = null }) {
  if (orderDidSucceed) {
    return (
      <Button
        m='24px auto 25px'
        width={1}
        data-cy='place-order'
        data-test='place-order'
        data-testid='place-order'
        disabled
      >
        Order complete
      </Button>
    );
  }

  const disabled = !isButtonEnabled || loading;

  return (
    <Button
      onClick={handleClickSubmit}
      m='24px auto 25px'
      width={1}
      data-cy='place-order'
      data-test='place-order'
      data-testid='place-order'
      disabled={disabled}
      height={submitButtonContents ? '52px' : '40px'}
    >
      {loading ? <SmallLoader height={20} color='#fff' /> : submitButtonContents || 'Place order'}
    </Button>
  );
}

const StyledLink = styled.a`
  color: var(--dcm-checkout-link-color, #0b99e6);
`;

const CustomTermsText = styled(Text)`
  text-align: center;
  font-size: 11px;
  line-height: 17px;
  color: var(--dcm-checkout-text-muted-color, #909ca7);
  margin-top: 12px;
  white-space: pre-wrap;
`;

const StyledCheckbox = styled(CheckboxOld)`
  padding-top: 1rem;
`;

export default observer(Submit);
